import {
    ExpressCheckoutElement,
    LinkAuthenticationElement,
    PaymentElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import Button from "../components/common/ui/buttons/Button";
import React, {useEffect, useRef, useState} from "react";
import ReCAPTCHA from "react-google-recaptcha-enterprise";
import PaymentInput from "./PaymentInput";
import {classNames, formatMoney, usePrevious} from "../components/common/Helpers";
import Skeleton from "react-loading-skeleton";
import {PulseLoader} from "react-spinners";
import {IoCard} from "react-icons/io5";
import {useDispatch, useSelector} from "react-redux";
import {paymentEntity} from "./store/root";

export default function PaymentInfoForm({ for: forObj, amountCents, totalCents, fieldErrors, setFieldErrors }) {
    const currency = window.organizationCurrency;
    const recaptchaKey = window.gck;
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);
    const [expressErrors, setExpressErrors] = useState([]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [showName, setShowName] = useState(false);
    const [expressCheckoutEnabled, setExpressCheckoutEnabled] = useState(true)
    const [expressCheckoutLoading, setExpressCheckoutLoading] = useState(true)
    const recaptchaRef = useRef();
    const dispatch = useDispatch();
    const { payment, paymentCreateLoading, paymentCreateError } = useSelector((state) => state.paymentReducer)
    const previous = usePrevious({ paymentCreateLoading });

    useEffect(() => {
        setExpressCheckoutLoading(true);
    }, [amountCents]);

    const makePayment = async (
        {
            total,
            for: forObj,
            name,
            email,
        },
    ) => {
        let recaptchaToken
        try {
            recaptchaToken = await recaptchaRef.current?.execute();
        } catch (e) {
           setErrors(['Recaptcha Failed']);
        }

        // call backend to create intent
        const payload = {
            total,
            for: forObj,
            name,
            email,
            token: recaptchaToken,
        };

        dispatch(paymentEntity.services.create(payload, { return: true }));
    }

    useEffect(async () => {
        if (paymentCreateLoading) {
            setSubmitting(true);
            return;
        }

        if (!previous?.paymentCreateLoading) {
            return;
        }

        if (paymentCreateError) {
            setErrors([paymentCreateError?.message || 'Internal Server Error'])
        }

        const clientSecret = payment.clientSecret;

        const currentUrl = new URL(window.location.href);
        const currentUrlStr = currentUrl.origin + currentUrl.pathname.replace(/\/$/, '');
        const returnUrl = `${currentUrlStr}/success`;

        const result = await stripe.confirmPayment({
            clientSecret,
            elements,
            confirmParams: {
                return_url: returnUrl,
            },
        });

        if (result.error) {
            setErrors([result.error.message]);
        }

        setSubmitting(false);
    }, [paymentCreateLoading]);

    const handleExpressPayment = async (e) => {
        makePayment({
            total: totalCents / 100,
            for: forObj,
            name: e.billingDetails.name,
            email: e.billingDetails.email,
        });
    };

    const handlePayment = async () => {
        let hasError = false;
        setFieldErrors({});
        setErrors([]);

        if (amountCents <= 0) {
            setFieldErrors((obj) => ({ ...obj, amount: 'Your amount is invalid.'}));
            hasError = true;
        }

        if (!forObj?.text) {
            setFieldErrors((obj) => ({ ...obj, for: 'Your reason for payment is incomplete.' }));
            hasError = true;
        }

        if (!name) {
            setFieldErrors((obj) => ({ ...obj, name: 'Your name is incomplete.'}));
            hasError = true;
        }

        const elResp = await elements.submit()
        if (elResp.error) {
            return;
        }

        if (hasError) return;
        if (!stripe) return;
        if (submitting) return;

        makePayment({
            total: totalCents / 100,
            for: forObj,
            name,
            email,
        });
    };

    const clearFieldError = (field) => () => {
        setFieldErrors((prev) => {
            return {...prev, [field]: null}
        })
    }

    const total = (totalCents / 100).toFixed(2);
    const amount = (amountCents / 100).toFixed(2);

    return (
        <>
            {expressErrors.length > 0 && (
                <div className="max-w-xl w-full pt-7 -mb-7">
                    {expressErrors.map((e) => (
                        <div key={e}
                             className="bg-white shadow-lg shadow-red-700/20 border border-red-100 text-red-700 p-3 px-4 rounded-lg text-sm mb-6">{e}</div>
                    ))}
                </div>
            )}


            {(!amountCents || amountCents >= 50) && expressCheckoutEnabled && (
                <div className="max-w-xl w-full mt-10 m-5 p-5 pb-2 border border-slate-400 rounded-xl relative">
                    <div className="absolute text-center w-full -top-3 left-0">
                        <h2 className="inline bg-slate-100 text-center px-4 text-sm">Quick Checkout</h2>
                    </div>
                    <ExpressCheckoutElement
                        onReady={(e) => {
                            if (!e.availablePaymentMethods) {
                                setExpressCheckoutEnabled(false);
                                setExpressCheckoutLoading(false);
                                return;
                            }

                            for (const [key, value] of Object.entries(e.availablePaymentMethods)) {
                                if (value) {
                                    setExpressCheckoutEnabled(true);
                                    setExpressCheckoutLoading(false);
                                    return;
                                }
                            }

                            setExpressCheckoutEnabled(false);
                        }}
                        onLoadError={(error) => setExpressCheckoutEnabled(false)}
                        onConfirm={handleExpressPayment}
                        onClick={({resolve, reject}) => {
                            setFieldErrors({});
                            setErrors([]);
                            let hasError = false;

                            if (amountCents <= 0) {
                                setFieldErrors((obj) => ({ ...obj, amount: 'Your amount is invalid.'}));
                                hasError = true;
                            }

                            if (!forObj?.text) {
                                setFieldErrors((obj) => ({ ...obj, for: 'Your reason for payment is incomplete.' }));
                                hasError = true;
                            }

                            if (hasError) {
                                reject();
                                return;
                            }

                            const options = {
                                emailRequired: true,
                            };
                            resolve(options);
                        }}
                        className="mb-3"
                        options={{
                            layout: {
                                maxColumns: 3,
                                maxRows: 1,
                            },
                        }}
                        key={amountCents}
                    />

                    {expressCheckoutLoading && (
                        <div className="flex w-full gap-3 mb-3">
                            <div className="flex-1">
                                <Skeleton
                                    baseColor={"#e2e6ed"}
                                    height={43}
                                />
                            </div>

                            <div className="flex-1">
                                <Skeleton
                                    baseColor={"#e2e6ed"}
                                    height={43}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="max-w-xl w-full m-5 px-2 -mb-2 flex gap-2 items-center">
                <IoCard size={20} className="text-slate-400"/>
                <h2 className="text-slate-500">Payment Information</h2>
            </div>

            <div className="max-w-xl w-full m-5 p-7 bg-white  shadow-lg shadow-slate-200 rounded-lg min-h-[300px]">
                <div>
                    <div className="min-h-[200px] mb-6">
                        <PaymentInput
                            label="Name"
                            name="name"
                            placeholder="Your Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            containerClass={classNames("mb-3 flex-1", !showName && "hidden")}
                            error={fieldErrors.name}
                            onClearError={clearFieldError('name')}
                        />

                        <LinkAuthenticationElement
                            className={classNames("mb-3 flex-1", !showName && "hidden")}
                            onReady={() => setShowName(true)}
                            onChange={(event) => {
                                setEmail(event.value.email);
                            }}
                        />

                        <PaymentElement/>
                    </div>

                    {errors.length > 0 && errors.map((e) => (
                        <div key={e}
                             className="shadow-lg shadow-red-700/20 border border-red-100 text-red-700 p-3 px-4 rounded-lg text-sm mb-6">{e}</div>
                    ))}

                    <ReCAPTCHA
                        size="invisible"
                        ref={recaptchaRef}
                        sitekey={recaptchaKey}
                    />

                    <Button
                        label={(
                            submitting ? (
                                <PulseLoader color="#ffffff" size={6} margin={2}/>
                            ) : (
                                amount > 0 ? `Pay ${formatMoney(total, false, currency)}` : 'Pay'
                            )
                        )}
                        color="blue"
                        className="w-full"
                        size="xl"
                        onClick={handlePayment}
                    />
                </div>
            </div>
        </>
    );
}
